import { Moon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import type { PageView, Module } from '@/types'
import type { useProgress } from '@/hooks/useProgress'

interface AppShellProps {
  modules: Module[]
  currentView: PageView
  onNavigate: (view: PageView) => void
  darkMode: boolean
  onToggleDark: () => void
  hasContentOverrides: boolean
  showDarkCallout: boolean
  onDismissDarkCallout: () => void
  progress: ReturnType<typeof useProgress>
  children: ReactNode
}

export function AppShell({
  modules,
  currentView,
  onNavigate,
  darkMode,
  onToggleDark,
  hasContentOverrides,
  showDarkCallout,
  onDismissDarkCallout,
  progress,
  children,
}: AppShellProps) {
  return (
    <div className="flex h-screen overflow-hidden bg-[var(--bg-secondary)]">
      <Sidebar
        modules={modules}
        currentView={currentView}
        onNavigate={onNavigate}
        progress={progress}
        hasContentOverrides={hasContentOverrides}
      />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar
          currentView={currentView}
          modules={modules}
          progress={progress}
          darkMode={darkMode}
          onToggleDark={onToggleDark}
          onNavigate={onNavigate}
        />
        <main className="flex-1 overflow-y-auto relative">
          {/* Dark mode callout — appears top-right, auto-dismisses */}
          {showDarkCallout && !darkMode && (
            <div className="absolute top-4 right-4 z-50 flex items-center gap-2.5 px-3 py-2 rounded-xl bg-[var(--brand-dark)] text-white text-xs shadow-[var(--shadow-lg)] animate-fade-in no-print">
              <Moon size={13} className="flex-shrink-0 opacity-70" />
              <span className="opacity-80">Dark mode available</span>
              <button
                onClick={() => { onToggleDark(); onDismissDarkCallout() }}
                className="font-semibold text-[var(--accent-blue)] hover:text-white transition-colors"
              >
                Enable
              </button>
              <button
                onClick={onDismissDarkCallout}
                className="opacity-50 hover:opacity-100 ml-1"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}
          <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
